// ./src/contexts/MembershipsContext/MembershipsContext.tsx
//
// Thin context wrapper around the useMemberships hook.
//
// All derived data lives in the hook itself.
//
// Usage:
//   <MembershipsProvider autoLoad>
//     <MyComponent />          {/* reads via useMembershipsContext() */}
//   </MembershipsProvider>
//
// Two accessors are exported:
//   useMembershipsContext()         — throws if no provider (for components that *require* it)
//   useOptionalMembershipsContext() — returns null (for components that can work with or without)

import React, { createContext, useContext } from "react";

import {
  useMemberships,
  UseMembershipsOptions,
  UseMembershipsReturn,
} from "@/hooks/useMemberships";

// ============================================
// Context
// ============================================

const MembershipsContext = createContext<UseMembershipsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface MembershipsProviderProps extends UseMembershipsOptions {
  children: React.ReactNode;
}

/**
 * Wraps children with a shared Memberships state.
 * Each provider instance creates its own independent hook,
 * so two providers on different pages won't interfere.
 */
export const MembershipsProvider: React.FC<MembershipsProviderProps> = ({
  children,
  ...options
}) => {
  const value = useMemberships(options);

  return (
    <MembershipsContext.Provider value={value}>
      {children}
    </MembershipsContext.Provider>
  );
};

// ============================================
// Strict accessor — throws when no provider
// ============================================

/**
 * Returns the Memberships context value.
 * Throws if called outside a <MembershipsProvider>.
 */
export const useMembershipsContext = (): UseMembershipsReturn => {
  const context = useContext(MembershipsContext);

  if (!context) {
    throw new Error(
      "useMembershipsContext must be used within a <MembershipsProvider>. " +
      "Wrap your component tree with <MembershipsProvider> to fix this."
    );
  }

  return context;
};

// ============================================
// Safe accessor — returns null when no provider
// ============================================

/**
 * Returns the Memberships context value, or null if no provider is present.
 * Use this in components that should work both inside and outside a provider
 * (e.g. reusable list/drawer components).
 */
export const useOptionalMembershipsContext = (): UseMembershipsReturn | null => {
  return useContext(MembershipsContext);
};
